import {
    SubscriptionBillingCadence,
    type SubscriptionOrderSummary,
} from '@houzy/schemas/partner-management/partner-subscription-management';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import TollIcon from '@mui/icons-material/Toll';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { formatSwissAmount } from '../../../lib/formatSwissAmount';
import { stateColor } from '../../../theme';
import { CheckoutSectionCard } from './CheckoutSectionCard';

export type CheckoutPricingCardProps = {
    offer: SubscriptionOrderSummary;
};

function Row({ label, value }: { label: ReactNode; value: ReactNode }): ReactElement {
    return (
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0, flex: 1 }}>
                {label}
            </Stack>
            <Stack sx={{ flexShrink: 0 }}>{value}</Stack>
        </Stack>
    );
}

// The catalogue is priced net, so every amount the partner is asked for carries the gross one under it. A zero amount
// does not — CHF 0.– incl. MWST says nothing.
function GrossAmountNote({ amount }: { amount: string }): ReactElement | null {
    const { t } = useTranslation();
    if (Number(amount) <= 0) {
        return null;
    }
    return (
        <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'right' }}>
            {t('CHECKOUT.PRICING.AMOUNT_INCL_VAT', { amount: formatSwissAmount(amount) })}
        </Typography>
    );
}

function CreditConvertedIcon(): ReactElement {
    return (
        <Box
            component="img"
            src="/checkout-icons/credit-converted.svg"
            alt=""
            aria-hidden
            sx={{ width: 14, height: 14, flexShrink: 0, display: 'block' }}
        />
    );
}

export function CheckoutPricingCard({ offer }: CheckoutPricingCardProps): ReactElement {
    const { t } = useTranslation();
    const { credits, initialFreeDays } = offer;
    // A credits-covered window is paid for, not free, so the two windows are only ever shown apart.
    const hasAnyFreeTime = initialFreeDays > 0 || (credits?.coverageDays ?? 0) > 0;

    // Upfront pays for the whole term in one charge, so the recurring amount is the term's price, not a month's.
    const isBilledMonthly = offer.plan.billingCadence === SubscriptionBillingCadence.Monthly;

    const title = credits ? t('CHECKOUT.PRICING.TITLE_BREAKDOWN') : t('CHECKOUT.PRICING.TITLE_SUMMARY');

    return (
        <CheckoutSectionCard title={title}>
            <Stack spacing={2}>
                {credits && (
                    <Paper
                        variant="outlined"
                        sx={{
                            bgcolor: 'background.default',
                            px: 2,
                            py: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                        }}>
                        <Avatar
                            sx={{
                                width: 40,
                                height: 40,
                                bgcolor: (theme) => stateColor(theme, 'focus', 'secondary'),
                                color: 'success.main',
                            }}>
                            <TollIcon />
                        </Avatar>
                        <Stack sx={{ flex: 1, minWidth: 0, py: 1 }}>
                            {/* The heading colour a tinted panel uses, which MUI only exposes on the alert palette. */}
                            <Typography variant="subtitle2" sx={{ color: 'Alert.successColor' }}>
                                {t('CHECKOUT.PRICING.CREDITS_TITLE')}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: 11, letterSpacing: '0.3px' }}>
                                {t('CHECKOUT.PRICING.CREDITS_SUBTITLE')}
                            </Typography>
                        </Stack>
                        <Stack sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: 30,
                                    fontWeight: 700,
                                    lineHeight: '40px',
                                    letterSpacing: '0.2px',
                                    color: 'success.main',
                                }}>
                                {credits.remainingCredits}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500, fontSize: 11, letterSpacing: '0.3px' }}>
                                {t('CHECKOUT.PRICING.CREDITS_UNIT')}
                            </Typography>
                        </Stack>
                    </Paper>
                )}

                {/* The breakdown reads as one ruled table: 4px between its rows, 16px away from what frames it. */}
                <Stack spacing={0.5}>
                    {hasAnyFreeTime ? (
                        <Stack
                            sx={{
                                // The leading rule separates the credits panel from the table; with no panel above,
                                // there is nothing to separate and the table opens straight under the card title.
                                borderTop: credits ? 1 : 0,
                                borderBottom: 1,
                                borderColor: 'divider',
                                py: 0.5,
                            }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ py: 0.5 }}>
                                {t('CHECKOUT.PRICING.FREE_PERIOD')}
                            </Typography>

                            {credits && credits.coverageDays > 0 && (
                                <Row
                                    label={
                                        <>
                                            <CreditConvertedIcon />
                                            <Stack sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" color="text.secondary" component="span">
                                                    {credits.pricePerCredit ? (
                                                        <Trans
                                                            i18nKey="CHECKOUT.PRICING.CREDIT_CONVERTED_WITH_RATE"
                                                            values={{
                                                                credits: credits.remainingCredits,
                                                                rate: formatSwissAmount(credits.pricePerCredit),
                                                                total: formatSwissAmount(
                                                                    credits.remainingMonetaryValue,
                                                                ),
                                                            }}
                                                            components={{
                                                                muted: (
                                                                    <Typography
                                                                        component="span"
                                                                        variant="body2"
                                                                        color="text.disabled"
                                                                    />
                                                                ),
                                                            }}
                                                        />
                                                    ) : (
                                                        <Trans
                                                            i18nKey="CHECKOUT.PRICING.CREDIT_CONVERTED"
                                                            values={{
                                                                credits: credits.remainingCredits,
                                                                total: formatSwissAmount(
                                                                    credits.remainingMonetaryValue,
                                                                ),
                                                            }}
                                                            components={{
                                                                muted: (
                                                                    <Typography
                                                                        component="span"
                                                                        variant="body2"
                                                                        color="text.disabled"
                                                                    />
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                </Typography>
                                                <Typography variant="caption" color="text.disabled">
                                                    {t('CHECKOUT.PRICING.CREDIT_CONVERTED_NOTE')}
                                                </Typography>
                                            </Stack>
                                        </>
                                    }
                                    value={
                                        <Typography variant="subtitle2" sx={{ color: 'success.main' }}>
                                            {t('CHECKOUT.DAYS', { days: credits.coverageDays })}
                                        </Typography>
                                    }
                                />
                            )}

                            {initialFreeDays > 0 && (
                                <Row
                                    label={
                                        <>
                                            <CardGiftcardIcon
                                                sx={{ fontSize: 14, flexShrink: 0, color: 'success.main' }}
                                            />
                                            <Stack sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('CHECKOUT.PRICING.BONUS_DAYS')}
                                                </Typography>
                                                {/* Sold days open at the end of the committed term, so listing them
                                                    beside a covered window would otherwise read as one free block
                                                    running before the first payment. */}
                                                <Typography variant="caption" color="text.disabled">
                                                    {t('CHECKOUT.PRICING.BONUS_DAYS_NOTE', {
                                                        months: offer.plan.commitmentLengthInMonths,
                                                    })}
                                                </Typography>
                                            </Stack>
                                        </>
                                    }
                                    value={
                                        <Typography variant="subtitle2" sx={{ color: 'success.main' }}>
                                            {t('CHECKOUT.DAYS', { days: initialFreeDays })}
                                        </Typography>
                                    }
                                />
                            )}
                        </Stack>
                    ) : (
                        credits && <Divider />
                    )}

                    <Stack spacing={2}>
                        <Row
                            label={
                                <Stack>
                                    <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
                                        {t('CHECKOUT.PRICING.FIRST_PAYMENT')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('CHECKOUT.PRICING.FIRST_PAYMENT_NOTE')}
                                    </Typography>
                                </Stack>
                            }
                            value={
                                <Stack sx={{ alignItems: 'flex-end' }}>
                                    <Typography variant="h6" sx={{ color: 'primary.main' }}>
                                        {t('CHECKOUT.AMOUNT', { amount: formatSwissAmount(offer.dueNowAmount) })}
                                    </Typography>
                                    <GrossAmountNote amount={offer.dueNowAmountInclVat} />
                                </Stack>
                            }
                        />

                        {offer.dueNowAmount !== offer.recurringAmount && (
                            <Row
                                label={
                                    <Typography variant="subtitle2">
                                        {t('CHECKOUT.PRICING.CHARGE_AFTER_FREE')}
                                    </Typography>
                                }
                                value={
                                    <Stack sx={{ alignItems: 'flex-end' }}>
                                        <Typography variant="subtitle2">
                                            {isBilledMonthly
                                                ? t('CHECKOUT.PRICING.AMOUNT_PER_MONTH', {
                                                      amount: formatSwissAmount(offer.recurringAmount),
                                                  })
                                                : t('CHECKOUT.PRICING.AMOUNT_PER_TERM', {
                                                      amount: formatSwissAmount(offer.recurringAmount),
                                                      months: offer.plan.commitmentLengthInMonths,
                                                  })}
                                        </Typography>
                                        <GrossAmountNote amount={offer.recurringAmountInclVat} />
                                    </Stack>
                                }
                            />
                        )}
                    </Stack>
                </Stack>

                {offer.autoProlongation && (
                    <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ alignItems: 'flex-start', pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                        <AutorenewIcon sx={{ fontSize: 16, color: 'action.active' }} />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500, fontSize: 11, letterSpacing: '0.3px' }}>
                            <Trans
                                i18nKey="CHECKOUT.PRICING.AUTO_RENEWS"
                                values={{ months: offer.renewsForMonths }}
                                components={{
                                    strong: (
                                        <Typography
                                            component="span"
                                            variant="caption"
                                            fontWeight="bold"
                                            color="text.primary"
                                            sx={{ fontSize: 11, letterSpacing: '0.3px' }}
                                        />
                                    ),
                                }}
                            />
                        </Typography>
                    </Stack>
                )}

                {!offer.autoProlongation && (
                    <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ alignItems: 'flex-start', pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                        <PaidOutlinedIcon sx={{ fontSize: 16, color: 'action.active' }} />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500, fontSize: 11, letterSpacing: '0.3px' }}>
                            {t('CHECKOUT.PRICING.NO_AUTO_RENEWAL')}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </CheckoutSectionCard>
    );
}
